import type { z } from 'zod'
import { HeroAsideLabelTip } from '@/components/case-study/HeroAsideLabelTip'
import { HERO_ASIDE_TOOLTIP_COPY } from '@/components/case-study/heroAsideTooltipCopy'
import { HeroToolLogos } from '@/components/case-study/HeroToolLogos'
import { longCaseStudyHeroSchema } from '@/domain/longCaseStudy'
import {
  formatAccelerationDisplay,
  formatHumanEffortDisplay,
  parseLikert5FromText,
} from '@/lib/caseStudyHeroFormat'

type Hero = z.infer<typeof longCaseStudyHeroSchema>

const labelClass =
  'text-[11px] font-semibold uppercase leading-[16.5px] tracking-[0.275px] text-[#636363]'

const valueClass = 'text-sm font-normal leading-[19.25px] text-[var(--foreground)]'

/** Caps label + info icon in one non-breaking row (icon glued after text, vertically centered). */
const LabelWithInlineTip = ({
  label,
  tipKey,
}: {
  label: string
  tipKey: keyof typeof HERO_ASIDE_TOOLTIP_COPY
}) => (
  <span
    className={`inline-flex max-w-full min-w-0 flex-nowrap items-center gap-0.5 whitespace-nowrap ${labelClass}`}
  >
    {label}
    <HeroAsideLabelTip label={label} explanation={HERO_ASIDE_TOOLTIP_COPY[tipKey]} />
  </span>
)

const HeroLikertBars = ({ score, label }: { score: number; label: string }) => {
  const clamped = Math.max(0, Math.min(5, Math.round(score)))
  return (
    <div
      className="flex shrink-0 items-center gap-0.5"
      role="img"
      aria-label={`${label}: ${clamped} out of 5`}
    >
      {Array.from({ length: 5 }, (_, i) => (
        <span
          key={i}
          className={`h-5 w-[3px] shrink-0 rounded-full ${
            i < clamped
              ? 'bg-[#202020]'
              : 'box-border border border-solid border-[#c6c6c6] bg-transparent'
          }`}
        />
      ))}
    </div>
  )
}

const FactStackRow = ({
  label,
  value,
  tipKey,
}: {
  label: string
  value: string
  /** When set, shows info control aligned to the label row without shifting title copy. */
  tipKey?: keyof typeof HERO_ASIDE_TOOLTIP_COPY
}) => (
  <div className="border-b border-[#e0e0e0] py-3 last:border-b-0">
    <div className="flex flex-col gap-1">
      {tipKey ? (
        <div className="min-w-0">
          <LabelWithInlineTip label={label} tipKey={tipKey} />
        </div>
      ) : (
        <div className={labelClass}>{label}</div>
      )}
      <div className={`${valueClass} [text-wrap:pretty]`}>{value}</div>
    </div>
  </div>
)

const MetricTextRow = ({
  label,
  value,
  tipKey,
}: {
  label: string
  value: string
  tipKey: keyof typeof HERO_ASIDE_TOOLTIP_COPY
}) => (
  <div className="flex min-h-11 items-center justify-between gap-1 border-b border-[#e0e0e0] py-3">
    <div className="flex max-w-[min(104px,45%)] min-w-0 shrink-0 items-center overflow-visible">
      <LabelWithInlineTip label={label} tipKey={tipKey} />
    </div>
    <div className="text-right text-sm font-bold leading-[19.25px] text-[var(--foreground)]">{value}</div>
  </div>
)

const MetricBarRow = ({
  label,
  score,
  fallbackText,
  tipKey,
}: {
  label: string
  score: number | null
  fallbackText: string
  tipKey: keyof typeof HERO_ASIDE_TOOLTIP_COPY
}) => (
  <div className="flex min-h-11 items-center justify-between gap-2 border-b border-[#e0e0e0] py-3">
    <div className="flex min-w-0 flex-1 items-center overflow-visible">
      <LabelWithInlineTip label={label} tipKey={tipKey} />
    </div>
    {score !== null ? (
      <HeroLikertBars score={score} label={label} />
    ) : (
      <div className="max-w-[60%] text-right text-sm font-normal leading-snug text-[var(--foreground)]">
        {fallbackText}
      </div>
    )}
  </div>
)

type Props = {
  hero: Hero
  /** When true, the client row is rendered elsewhere (e.g. above the title). */
  hideClient?: boolean
  /** When true, industry is shown next to the client name above the title instead. */
  hideIndustry?: boolean
  /** When true, focus area is shown only as the filled badge under the title. */
  hideFocusArea?: boolean
}

export const CaseStudyHeroAside = ({ hero, hideClient, hideIndustry, hideFocusArea }: Props) => {
  const aiScore = parseLikert5FromText(hero.ai_handoff_potential)
  const replScore = parseLikert5FromText(hero.replicability)

  const metricsCard =
    hero.human_effort ||
    hero.acceleration ||
    hero.ai_handoff_potential ||
    hero.replicability ||
    (hero.tools && hero.tools.length > 0)

  const factsCard =
    (!hideClient && hero.client) ||
    (!hideIndustry && hero.industry) ||
    hero.core_problem ||
    (!hideFocusArea && hero.focus_area)

  return (
    <aside
      className={
        'mx-auto flex w-full min-w-0 max-w-[448px] shrink flex-col gap-8 lg:mx-0 lg:w-[252px] lg:max-w-[252px] lg:self-start'
      }
    >
      {factsCard ? (
        <div className="flex flex-col">
          {!hideClient && hero.client ? (
            <FactStackRow tipKey="client" label="Client" value={hero.client} />
          ) : null}
          {!hideIndustry && hero.industry ? (
            <FactStackRow tipKey="industry" label="Industry" value={hero.industry} />
          ) : null}
          {hero.core_problem ? (
            <FactStackRow label="Core problem" value={hero.core_problem} />
          ) : null}
          {!hideFocusArea && hero.focus_area ? (
            <FactStackRow tipKey="focus_area" label="Focus area" value={hero.focus_area} />
          ) : null}
        </div>
      ) : null}

      {metricsCard ? (
        <div className="flex flex-col">
          {hero.human_effort ? (
            <MetricTextRow
              tipKey="human_effort"
              label="Case build time"
              value={formatHumanEffortDisplay(hero.human_effort)}
            />
          ) : null}
          {hero.acceleration ? (
            <MetricTextRow
              tipKey="acceleration"
              label="Acceleration"
              value={formatAccelerationDisplay(hero.acceleration)}
            />
          ) : null}
          {hero.ai_handoff_potential ? (
            <MetricBarRow
              tipKey="ai_handoff"
              label="AI handoff"
              score={aiScore}
              fallbackText={hero.ai_handoff_potential}
            />
          ) : null}
          {hero.replicability ? (
            <MetricBarRow
              tipKey="replicability"
              label="Replicability"
              score={replScore}
              fallbackText={hero.replicability}
            />
          ) : null}
          {hero.tools?.length ? (
            <div className="flex flex-col gap-1 pt-3">
              <div className={labelClass}>Tools</div>
              <HeroToolLogos tools={hero.tools} />
            </div>
          ) : null}
        </div>
      ) : null}
    </aside>
  )
}
